export const appRoutes = {
  home: '/',
  market: (marketId: number | string) => `/market/${marketId}`,
  portfolio: '/portfolio',
  leaderboard: '/leaderboard',
  help: '/help',
  whitepaper: '/whitepaper',
  xpred: '/xpred',
  resolution: '/resolution',
  resolutionDetail: (tab: string, marketId: number | string) => `/resolution/${tab}/${marketId}`,
  income: '/income',
  proposals: '/proposals',
  proposalNew: '/proposals/new',
  proposalDetail: (marketId: number | string) => `/proposals/${marketId}`,
  upcoming: '/upcoming',
  upcomingDetail: (marketId: number | string) => `/upcoming/${marketId}`,
  superadmin: '/superadmin',
  profile: (account: string) => `/profile/${account}`,
} as const;

export type TopNavSection =
  | 'markets'
  | 'upcoming'
  | 'portfolio'
  | 'superadmin'
  | 'leaderboard'
  | 'help'
  | 'whatisxpred'
  | 'resolution'
  | 'income'
  | null;

export const resolutionTabs = ['open', 'voting', 'stalled', 'closed', 'illiquid'] as const;
export type ResolutionTab = typeof resolutionTabs[number];

export const getTopNavSection = (pathname: string): TopNavSection => {
  if (pathname === appRoutes.home || pathname.startsWith('/market/')) {
    return 'markets';
  }
  if (pathname.startsWith('/upcoming')) {
    return 'upcoming';
  }
  if (pathname.startsWith('/portfolio')) {
    return 'portfolio';
  }
  if (pathname.startsWith('/superadmin')) {
    return 'superadmin';
  }
  if (pathname.startsWith('/leaderboard') || pathname.startsWith('/profile/')) {
    return 'leaderboard';
  }
  if (pathname.startsWith('/help')) {
    return 'help';
  }
  if (pathname.startsWith('/xpred')) {
    return 'whatisxpred';
  }
  if (pathname.startsWith('/resolution')) {
    return 'resolution';
  }
  if (pathname.startsWith('/income')) {
    return 'income';
  }
  return null;
};

export const getLegacyRedirectPath = (pathname: string, search: string): string | null => {
  const url = new URL(`https://example.local${pathname}${search}`);
  const page = url.searchParams.get('page');
  const market = url.searchParams.get('market');

  if (page === 'help') return appRoutes.help;
  if (page === 'whitepaper') return appRoutes.whitepaper;
  if (page === 'xpred') return appRoutes.xpred;
  if (page === 'portfolio') return appRoutes.portfolio;
  if (page === 'superadmin') return appRoutes.superadmin;
  if (page === 'leaderboard') return appRoutes.leaderboard;
  if (page === 'resolution') return appRoutes.resolution;
  if (page === 'myproposals') return appRoutes.proposals;
  if (page === 'addproposal') return appRoutes.proposalNew;
  if (page === 'upcoming-markets') return appRoutes.upcoming;
  if (page === 'income') return appRoutes.income;

  if (page === 'proposaldetail' && market) {
    return appRoutes.proposalDetail(market);
  }

  if (page === 'upcoming-markets-detail' && market) {
    return appRoutes.upcomingDetail(market);
  }

  if (!page && market && pathname === appRoutes.home) {
    return appRoutes.market(market);
  }

  return null;
};

export const getInternalNavigationTarget = (rawLink: string, origin: string): string | null => {
  try {
    const url = new URL(rawLink, origin);
    if (url.origin !== origin) {
      return null;
    }

    return (
      getLegacyRedirectPath(url.pathname, url.search) ??
      `${url.pathname}${url.search}${url.hash}`
    );
  } catch (error) {
    if (!rawLink.startsWith('/')) {
      return null;
    }

    const redirect = getLegacyRedirectPath(appRoutes.home, rawLink.startsWith('/?') ? rawLink.slice(1) : '');
    return redirect ?? rawLink;
  }
};

export const parseIntegerParam = (value: string | undefined): number | null => {
  if (!value) {
    return null;
  }

  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

export const isResolutionTab = (value: string | undefined): value is ResolutionTab =>
  resolutionTabs.includes(value as ResolutionTab);
